import { createWorkerRequestClient } from '@/utils/workerRequestClient'

export interface CutoutWorkerRequest {
  /** The removal API's own mask, before it is unsquashed to the photo's shape. */
  maskBase64: string
  /** Photo dimensions over model-input dimensions, per axis — the model input is
   *  squashed to a fixed size, so these differ. */
  ratioX: number
  ratioY: number
  originalDataUrl: string
  maskThreshold: number
}

export interface CutoutWorkerResponse {
  id: number
  error?: string
  /** The mask at the photo's dimensions, PNG. */
  maskBlob?: Blob
  /** The photo with the mask as its alpha, PNG. */
  cutoutBlob?: Blob
  /** Bounding box of the kept subject, in mask pixels. */
  rect?: { x: number; y: number; width: number; height: number }
  width?: number
  height?: number
}

/** What the worker produced, all of it or nothing. */
export interface CutoutWorkerResult {
  maskBlob: Blob
  cutoutBlob: Blob
  rect: { x: number; y: number; width: number; height: number }
  width: number
  height: number
}

/**
 * How many cutout requests may be inside the worker at once.
 *
 * Removal runs twenty wide because the API call is the slow part, but a cutout
 * request carries a full-size photo data URL and a mask, and `postMessage` copies
 * both — a copy that lives until the worker answers. The worker builds one cutout
 * at a time, so posting all twenty keeps twenty copies alive to no purpose. One
 * being worked on plus one already queued behind it keeps the worker fed without
 * a pile of full-size payloads; the rest wait on the main thread, where they cost
 * only the strings their caller already holds.
 */
export const CUTOUT_WORKER_MAX_IN_FLIGHT = 2

const client = createWorkerRequestClient<CutoutWorkerRequest, CutoutWorkerResponse>(
  'cutoutWorker',
  () => import('./cutoutWorkerSpawn').then((module) => module.spawnCutoutWorker()),
  { maxInFlight: CUTOUT_WORKER_MAX_IN_FLIGHT },
)

/**
 * Throws away every cutout the worker is still carrying and kills it, so a
 * cancelled pass leaves nothing behind for the next one to wait on. The next
 * request starts a fresh worker.
 */
export function resetCutoutWorker(): void {
  client.reset()
}

/**
 * Mask, cutout and bounding box for one photo, or `null` if the worker couldn't
 * produce them — see `createWorkerRequestClient`, and `removeBackground` for the
 * main-thread fallback it falls back to. `null` is also the answer for a request
 * `abortSignal` dropped, so callers must check the signal before falling back.
 */
export async function tryBuildCutoutInWorker(
  request: CutoutWorkerRequest,
  abortSignal?: AbortSignal,
): Promise<CutoutWorkerResult | null> {
  const result = await client.tryRequest(request, abortSignal)
  if (!result?.maskBlob || !result.cutoutBlob || !result.rect || !result.width || !result.height) {
    return null
  }
  return {
    maskBlob: result.maskBlob,
    cutoutBlob: result.cutoutBlob,
    rect: result.rect,
    width: result.width,
    height: result.height,
  }
}
